import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

function formatoNumerico(value) {
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatoPesos(value) {
    return '$ ' + value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function FormatoFechaSinHora(value) {
    const fecha = new Date(value);
    return format(fecha, 'dd-MMMM-yyyy', { locale: es }).toUpperCase();
}

function FormatoFechaConHora(value) {
    const fecha = new Date(value);
    return format(fecha, "dd-MMMM-yyyy HH:mm:ss", { locale: es }).toUpperCase();
}

// Espera a que la imagen cargue y devuelve ancho proporcional a la altura dada
function calcularAnchoProporcional(base64, alturaFija) {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const ancho = img.naturalWidth && img.naturalHeight
                ? (img.naturalWidth * alturaFija) / img.naturalHeight
                : 50;
            resolve(ancho);
        };
        img.onerror = () => resolve(50); // fallback si falla la carga
        img.src = base64;
    });
}

// Detecta el formato de imagen a partir del base64
function detectarFormatoImagen(base64) {
    if (base64.startsWith('data:image/png'))  return 'PNG';
    if (base64.startsWith('data:image/jpeg')) return 'JPEG';
    if (base64.startsWith('data:image/jpg'))  return 'JPEG';
    if (base64.startsWith('data:image/gif'))  return 'GIF';
    if (base64.startsWith('data:image/webp')) return 'WEBP';
    return 'PNG'; // fallback
}

const AuxiliarPdf = async (ObjAuxiliar, objEmpresa, fechaI, fechaF, cuenta) => {
    return new Promise(async (resolve, reject) => {
        try {
            const doc = new jsPDF({ format: 'letter' });

            const color              = objEmpresa.color || '#662e91';
            const logoBase64         = objEmpresa.logo?.base64 || '';
            const fechaHoraGeneracion = FormatoFechaConHora(new Date());

            const margenIzq  = 12;
            const margenDer  = 12;
            const anchoUtil  = 215 - margenIzq - margenDer; // ~191mm en carta

            // ── CABECERA ──────────────────────────────────────────────────
            let y = 14;

            // Logo — espera carga real para calcular ancho proporcional
            if (logoBase64 !== '') {
                const alturaLogo = 20;
                const formatoImg = detectarFormatoImagen(logoBase64);
                const anchoLogo  = await calcularAnchoProporcional(logoBase64, alturaLogo);
                doc.addImage(logoBase64, formatoImg, margenIzq, y -5, anchoLogo, alturaLogo);
            }

            // Datos empresa — alineados a la derecha
            const xDer = 215 - margenDer;
            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.text('Mayores y auxiliares', xDer, y, { align: 'right' });

            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.text(objEmpresa.rfc + ' | ' + objEmpresa.nombre, xDer, y += 6, { align: 'right' });

            const periodo = 'PERIODO DEL ' + FormatoFechaSinHora(fechaI) + ' AL ' + FormatoFechaSinHora(fechaF);
            doc.text(periodo, xDer, y += 5, { align: 'right' });

            y += 10;

            // ── TABLA DE TOTALES (autoTable) ──────────────────────────────
            doc.autoTable({
                startY: y,
                head: [[
                    { content: 'Cuenta',  styles: { halign: 'left'  } },
                    { content: 'Inicial', styles: { halign: 'right' } },
                    { content: 'Cargo',   styles: { halign: 'right' } },
                    { content: 'Abono',   styles: { halign: 'right' } },
                    { content: 'Final',   styles: { halign: 'right' } },
                ]],
                body: [[
                    { content: cuenta.numCuenta,                                                styles: { halign: 'left',  fontStyle: 'bold' } },
                    { content: formatoPesos(cuenta.cargoInicial - cuenta.abonoInicial),         styles: { halign: 'right' } },
                    { content: formatoPesos(cuenta.cargo),                                      styles: { halign: 'right' } },
                    { content: formatoPesos(cuenta.abono),                                      styles: { halign: 'right' } },
                    { content: formatoPesos(cuenta.cargoFinal  - cuenta.abonoFinal),            styles: { halign: 'right' } },
                ]],
                styles: {
                    fontSize: 8,
                    cellPadding: 2,
                },
                headStyles: {
                    fillColor: color,
                    textColor: '#ffffff',
                    fontStyle: 'bold',
                },
                bodyStyles: {
                    fillColor: '#f9f9f9',
                },
                margin: { left: margenIzq, right: margenDer },
                tableWidth: anchoUtil,
            });

            y = doc.lastAutoTable.finalY + 6;

            // ── TABLA DE MOVIMIENTOS (autoTable) ─────────────────────────
            const head = [[
                { content: 'Fecha',        styles: { halign: 'left'  } },
                { content: 'Póliza',       styles: { halign: 'left'  } },
                { content: 'Ref. Asiento', styles: { halign: 'left'  } },
                { content: 'Descripción',  styles: { halign: 'left'  } },
                { content: 'Cargo',        styles: { halign: 'right' } },
                { content: 'Abono',        styles: { halign: 'right' } },
            ]];

            const body = ObjAuxiliar.map(m => ([
                { content: FormatoFechaSinHora(m.fecha),                    styles: { halign: 'left'  } },
                { content: m.tipo.substring(0, 1) + ' | ' + m.numPoliza,   styles: { halign: 'left'  } },
                { content: m.descripcionAsiento || '',                      styles: { halign: 'left'  } },
                { content: m.descripcionPoliza  || '',                      styles: { halign: 'left'  } },
                { content: formatoPesos(m.cargo),                           styles: { halign: 'right' } },
                { content: formatoPesos(m.abono),                           styles: { halign: 'right' } },
            ]));

            doc.autoTable({
                startY: y,
                head,
                body,
                styles: {
                    fontSize: 6,
                    cellPadding: 2,
                    overflow: 'linebreak',
                },
                headStyles: {
                    fillColor: color,
                    textColor: '#ffffff',
                    fontStyle: 'bold',
                },
                alternateRowStyles: {
                    fillColor: '#f4f4f4',
                },
                columnStyles: {
                    0: { cellWidth: 28  },  // Fecha
                    1: { cellWidth: 20  },  // Póliza
                    2: { cellWidth: 38  },  // Ref. Asiento
                    3: { cellWidth: 60  },  // Descripción
                    4: { cellWidth: 22  },  // Cargo
                    5: { cellWidth: 23  },  // Abono
                },
                margin: { left: margenIzq, right: margenDer },
                tableWidth: anchoUtil,
            });

            // ── PIE DE PÁGINA ─────────────────────────────────────────────
            const totalPages = doc.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
                doc.setPage(i);
                doc.setFont('helvetica', 'italic');
                doc.setFontSize(8);
                doc.setTextColor(150);

                const pageH = doc.internal.pageSize.getHeight();
                const pageW = doc.internal.pageSize.getWidth();

                // Línea separadora
                doc.setDrawColor(color);
                doc.setLineWidth(0.3);
                doc.line(margenIzq, pageH - 16, pageW - margenDer, pageH - 16);

                // Fecha y hora de generación (izquierda)
                doc.text('Generado: ' + fechaHoraGeneracion, margenIzq, pageH - 8, { align: 'left' });

                // Leyenda (centro)
                doc.text('Documento generado por ContaGo', pageW / 2, pageH - 8, { align: 'center' });

                // Número de página (derecha)
                doc.text('Página ' + i + ' de ' + totalPages, pageW - margenDer, pageH - 8, { align: 'right' });
            }

            const base64 = doc.output('datauristring');
            resolve(base64);
        } catch (error) {
            console.error(error);
            reject(error);
        }
    });
};

export { AuxiliarPdf };